import { useState, useMemo, useCallback, useEffect } from 'react';
import { NPSDSearchFilters } from '@/components/npsd/NPSDSearchFilters';
import { NPSDSearchResults } from '@/components/npsd/NPSDSearchResults';
import { NPSDDateRangePicker } from '@/components/npsd/NPSDDateRangePicker';
import { Dropdownlist } from '@/components/Dropdownlist';

import { api } from '@/services/api-class';
import moment from 'moment';

interface NPSDSearchTourStopProps {
  searchMode?: string;
  isMobile?: boolean;
  theme?: string;
  officerLabel?: string;
  onView?: (item: any) => void;
  onOpenNewTab?: (item: any) => void;
}

export function NPSDSearchTourStop({
  searchMode = 'default',
  isMobile = false,
  theme = '',
  officerLabel = 'Officer',
  onView,
  onOpenNewTab,
}: NPSDSearchTourStopProps) {
  // Search form fields
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [npsdClientId, setNpsdClientId] = useState<string | number | null>('');
  const [officerId, setOfficerId] = useState<string | number | null>('');
  const [tourId, setTourId] = useState<string | number | null>('');

  // Dropdown options
  const [clients, setClients] = useState<any[]>([]);
  const [officers, setOfficers] = useState<any[]>([]);
  const [tours, setTours] = useState<any[]>([]);

  // Results
  const [results, setResults] = useState<any[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [isLoadingOptions, setIsLoadingOptions] = useState(true);

  const columns = useMemo(() => [
    { key: 'tourStopTitle', label: 'Tour Stop', field: 'tourStopTitle' },
    { key: 'tourTitle', label: 'Tour', field: 'tourTitle' },
    { key: 'clientName', label: 'Client', field: 'clientName' },
    { key: 'officerDisplay', label: officerLabel, field: 'officerDisplay' },
    { key: 'tasks', label: 'Tasks', field: 'tasks' },
    { key: 'issues', label: 'Issues', field: 'issues' },
    { key: 'completedAt', label: 'Completed At', field: 'completedAt' },
  ], [officerLabel]);

  const clientItems = useMemo(() => {
    return clients.map((c: any) => ({ label: c.name, value: c.id }));
  }, [clients]);

  const officerItems = useMemo(() => {
    return officers.map((o: any) => {
      const name = o.name;
      let label: string;
      if (typeof name === 'string') {
        label = name;
      } else if (name && Array.isArray(name.strings) && Array.isArray(name.values)) {
        label = name.strings.reduce((acc: string, str: string, i: number) =>
          acc + (i > 0 ? (name.values[i - 1] || '') : '') + str, '');
      } else {
        label = String(name || '');
      }
      return { label, value: o.id };
    });
  }, [officers]);

  const tourItems = useMemo(() => {
    return tours.map((t: any) => ({ label: t.name, value: t.id }));
  }, [tours]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (startDate) count++;
    if (endDate) count++;
    if (npsdClientId) count++;
    if (officerId) count++;
    if (tourId) count++;
    return count;
  }, [startDate, endDate, npsdClientId, officerId, tourId]);

  // Load dropdown options on mount
  useEffect(() => {
    let loaded = 0;
    const checkDone = () => {
      loaded++;
      if (loaded >= 3) {
        setIsLoadingOptions(false);
      }
    };

    // Load clients
    api.listClientGroup((result: any) => {
      if (result && result.npsdClientList) {
        setClients(result.npsdClientList.sort((a: any, b: any) =>
          (a.name || '').localeCompare(b.name || '')
        ));
      }
      checkDone();
    });

    // Load officers with completions
    api.getTourStopCompletionOfficers((result: any) => {
      if (Array.isArray(result)) {
        setOfficers(result);
      }
      checkDone();
    });

    // Load active tours
    api.getTourStopCompletionTours((result: any) => {
      if (Array.isArray(result)) {
        setTours(result);
      }
      checkDone();
    });
  }, []);

  const loadResults = useCallback((page: number, size: number) => {
    setIsLoading(true);

    const filters: any[] = [];

    if (startDate) {
      const startMoment = moment(startDate);
      if (startMoment.isValid()) {
        filters.push({ field: 'completedAtStart', operator: 'gte', value: startMoment.utc().format((window as any).DATETIME_FORMAT_SERVER_UTC) });
      }
    }
    if (endDate) {
      const endMoment = moment(endDate);
      if (endMoment.isValid()) {
        filters.push({ field: 'completedAtEnd', operator: 'lte', value: endMoment.add(23, 'hours').add(59, 'minutes').add(59, 'seconds').add(999, 'milliseconds').utc().format((window as any).DATETIME_FORMAT_SERVER_UTC) });
      }
    }
    if (npsdClientId) {
      filters.push({ field: 'npsdClientId', operator: 'eq', value: String(npsdClientId) });
    }
    if (officerId) {
      filters.push({ field: 'officerId', operator: 'eq', value: String(officerId) });
    }
    if (tourId) {
      filters.push({ field: 'tourId', operator: 'eq', value: String(tourId) });
    }

    const searchData = {
      data: {
        take: size,
        skip: (page - 1) * size,
        page: page,
        pageSize: size,
        filter: {
          logic: 'and',
          filters: filters,
        },
      },
    };

    api.searchTourStopCompletions(
      JSON.stringify(searchData),
      (result: any) => {
        setResults(result.entries || []);
        setTotalRecords(result.records || 0);
        setHasSearched(true);
        setIsLoading(false);
      }
    );
  }, [startDate, endDate, npsdClientId, officerId, tourId]);

  const performSearch = useCallback(() => {
    setCurrentPage(1);
    loadResults(1, pageSize);
  }, [loadResults, pageSize]);

  const clearSearch = useCallback(() => {
    setStartDate(null);
    setEndDate(null);
    setNpsdClientId('');
    setOfficerId('');
    setTourId('');
    setResults([]);
    setTotalRecords(0);
    setCurrentPage(1);
    setHasSearched(false);
  }, []);

  const handleView = useCallback((item: any) => {
    onView?.(item);
  }, [onView]);

  const handleOpenNewTab = useCallback((item: any) => {
    onOpenNewTab?.(item);
  }, [onOpenNewTab]);

  const handleRowClick = useCallback((item: any) => {
    handleView(item);
  }, [handleView]);

  const onPageChange = useCallback((page: number) => {
    setCurrentPage(page);
    loadResults(page, pageSize);
  }, [loadResults, pageSize]);

  const onPageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setCurrentPage(1);
    loadResults(1, size);
  }, [loadResults]);

  const formatDateTime = useCallback((dateStr: string): string => {
    if (!dateStr) return '\u2014';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    return date.toLocaleString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }, []);

  const formatTasks = useCallback((item: any): string => {
    return `${item.tasksCompleted ?? 0}/${item.tasksTotal ?? 0}`;
  }, []);

  const formatIssues = useCallback((item: any): string => {
    const parts: string[] = [];
    if (item.newIssues > 0) parts.push(`${item.newIssues} new`);
    if (item.fixedIssues > 0) parts.push(`${item.fixedIssues} fixed`);
    if (item.residualIssues > 0) parts.push(`${item.residualIssues} residual`);
    return parts.length > 0 ? parts.join(', ') : '\u2014';
  }, []);

  const formatOfficer = useCallback((item: any): string => {
    const name = item.officerName || '';
    const callSign = item.officerCallSign;
    return callSign ? `${name} (${callSign})` : name;
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      performSearch();
    }
  }, [performSearch]);

  const cellRenderers = useMemo(() => ({
    completedAt: (item: any) => formatDateTime(item.completedAt),
    officerDisplay: (item: any) => formatOfficer(item),
    tasks: (item: any) => formatTasks(item),
    issues: (item: any) => formatIssues(item),
  }), [formatDateTime, formatOfficer, formatTasks, formatIssues]);

  const renderCardTitle = useCallback((item: any) => item.tourStopTitle, []);

  return (
    <div className={`npsd-search-tourstop ${theme}`}>
      <NPSDSearchFilters
        activeFilterCount={activeFilterCount}
        loading={isLoading || isLoadingOptions}
        onSearch={performSearch}
      >
        <div className="search-fields">
          {/* Date Range Section */}
          <NPSDDateRangePicker
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
          />

          {/* Other Filters */}
          <div className="field-row">
            <div className="field">
              <label>Client</label>
              <Dropdownlist
                value={npsdClientId}
                items={clientItems}
                labelKey="label"
                valueKey="value"
                placeholder="Select Client"
                clearable={true}
                onChange={setNpsdClientId}
              />
            </div>
            <div className="field">
              <label>{officerLabel}</label>
              <Dropdownlist
                value={officerId}
                items={officerItems}
                labelKey="label"
                valueKey="value"
                placeholder={'Select ' + officerLabel}
                clearable={true}
                onChange={setOfficerId}
              />
            </div>
            <div className="field">
              <label>Tour</label>
              <Dropdownlist
                value={tourId}
                items={tourItems}
                labelKey="label"
                valueKey="value"
                placeholder="Select Tour"
                clearable={true}
                onChange={setTourId}
              />
            </div>
          </div>
        </div>
        <div className="search-actions">
          <button type="button" className="btn btn-primary" onClick={performSearch} disabled={isLoading || isLoadingOptions}>
            <i className="fa fa-search"></i> Search
          </button>
          <button type="button" className="btn btn-secondary" onClick={clearSearch}>
            <i className="fa fa-trash"></i> Clear
          </button>
        </div>
      </NPSDSearchFilters>

      {/* Results */}
      <NPSDSearchResults
        columns={columns}
        results={results}
        loading={isLoading}
        hasSearched={hasSearched}
        totalRecords={totalRecords}
        currentPage={currentPage}
        pageSize={pageSize}
        showNewTabButton={true}
        cellRenderers={cellRenderers}
        renderCardTitle={renderCardTitle}
        onRowClick={handleRowClick}
        onLinkClick={handleView}
        onOpenNewTab={handleOpenNewTab}
        onCurrentPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />
    </div>
  );
}
